import { supportApi, type SupportApiClient } from "./supportApi";
import { hasValidEmail, hasValue, validateSupportForm } from "./supportFormRules";
import type { SupportSubmissionRequest } from "./supportTypes";
import type { MemberProfile } from "@/features/member-accounts/memberProfile";

export class SupportPageController {
  private page: HTMLElement | null = null;
  private inputCountry: HTMLInputElement | null = null;
  private inputName: HTMLInputElement | null = null;
  private inputEmail: HTMLInputElement | null = null;
  private inputMessage: HTMLTextAreaElement | null = null;
  private countrySelect: HTMLElement | null = null;
  private submitButton: HTMLElement | null = null;
  private captchaInput: HTMLInputElement | null = null;
  private loggedIn: MemberProfile | undefined;

  constructor(private readonly api: SupportApiClient = supportApi) {}

  init(): void {
    this.page = document.querySelector(".support-page");
    if (!this.page) {
      return;
    }

    this.inputCountry = document.getElementById("SupportCountry") as HTMLInputElement | null;
    this.inputName = document.getElementById("SupportName") as HTMLInputElement | null;
    this.inputEmail = document.getElementById("SupportEmail") as HTMLInputElement | null;
    this.inputMessage = document.getElementById("SupportMessage") as HTMLTextAreaElement | null;
    this.countrySelect = document.getElementById("SelectCountry");
    this.submitButton = document.getElementById("supportSubmitBtn");
    this.captchaInput = document.getElementById("Captcha") as HTMLInputElement | null;
    this.loggedIn = window.memberInfo;

    this.updateBackButton();
    this.setListeners();
  }

  private updateBackButton(): void {
    const closeButton = this.page?.querySelector(".close-btn") as HTMLAnchorElement | null;

    // Only same-site referrers are safe replacements for the localized fallback link from Razor.
    if (closeButton && document.referrer.startsWith(window.location.origin)) {
      closeButton.href = document.referrer;
    }
  }

  private setListeners(): void {
    this.inputMessage?.addEventListener("input", (event) =>
      this.validateInput(event.currentTarget as HTMLElement),
    );
    this.inputEmail?.addEventListener("input", (event) =>
      this.validateEmail(event.currentTarget as HTMLInputElement),
    );
    this.inputName?.addEventListener("input", (event) =>
      this.validateInput(event.currentTarget as HTMLElement),
    );

    if (this.countrySelect) {
      this.countrySelect.addEventListener("click", (event) => {
        event.preventDefault();
        this.countrySelect?.parentElement?.classList.toggle("is-open");
      });

      const countryOptions = this.countrySelect.parentElement?.querySelectorAll(".FilterList-inner a");
      countryOptions?.forEach((option) => {
        option.addEventListener("click", (event) => {
          event.preventDefault();
          const target = event.currentTarget as HTMLAnchorElement;
          if (this.inputCountry) {
            this.inputCountry.value = target.getAttribute("data-value") || "";
          }

          this.validateCountrySelector();
          const filterButtonValue = document.querySelector(".FilterButton-value");
          if (filterButtonValue) {
            filterButtonValue.textContent = target.textContent;
          }

          this.countrySelect?.parentElement?.classList.remove("is-open");
        });
      });

      document.addEventListener(
        "click",
        (event) => {
          const target = event.target as HTMLElement;
          const clickedList = this.countrySelect?.parentElement?.contains(target) || false;
          const clickedButton = this.countrySelect?.contains(target) || false;
          if (!clickedList && !clickedButton) {
            this.countrySelect?.parentElement?.classList.remove("is-open");
          }
        },
        true,
      );
    }

    if (!this.loggedIn) {
      this.initCaptcha();
    }

    this.submitButton?.addEventListener("click", (event) => {
      event.preventDefault();
      void this.submit();
    });
  }

  private initCaptcha(): void {
    document.querySelector(".captcha-update")?.addEventListener("click", () => {
      void this.updateCaptcha();
      if (this.captchaInput) {
        this.captchaInput.value = "";
      }
    });
  }

  private async updateCaptcha(): Promise<void> {
    const captchaImage = document.querySelector(".captcha-image") as HTMLImageElement | null;
    if (!captchaImage) {
      return;
    }

    const captchaIdUrl = captchaImage.getAttribute("data-id-url");
    const captchaImageUrl = captchaImage.getAttribute("data-image-url");
    if (!captchaIdUrl) {
      return;
    }

    const newId = await this.api.getCaptchaId(captchaIdUrl);
    captchaImage.src = `${captchaImageUrl}?id=${newId}`;
    captchaImage.setAttribute("data-id", newId);
  }

  private getCaptchaId(): string | null {
    return document.querySelector(".captcha-image")?.getAttribute("data-id") ?? null;
  }

  private async submit(): Promise<void> {
    const request = this.validateInputs();
    if (!request) {
      return;
    }

    this.submitButton?.classList.add("loading");
    try {
      const response = await this.api.submit(request);
      if (response?.success) {
        this.page?.classList.add("request-sent");
        this.page?.classList.remove("request-error");
      } else if (response?.captcha === false) {
        this.invalidateInput(this.captchaInput);
        if (this.captchaInput) {
          this.captchaInput.value = "";
        }
        void this.updateCaptcha();
      } else {
        this.page?.classList.add("request-error");
      }
    } catch (error) {
      console.log(error);
      this.page?.classList.add("request-error");
    } finally {
      this.submitButton?.classList.remove("loading");
    }
  }

  private validateInputs(): SupportSubmissionRequest | false {
    const request: SupportSubmissionRequest = {
      name: this.inputName?.value || "",
      email: this.inputEmail?.value || "",
      country: this.inputCountry?.value || "",
      message: this.inputMessage?.value || "",
      captchaInput: this.captchaInput?.value || "",
      captchaId: this.getCaptchaId(),
    };
    const validation = validateSupportForm(
      request,
      Boolean(this.inputCountry),
      !this.loggedIn && Boolean(this.captchaInput),
    );

    // Keep the established short-circuit order because it controls which field is marked first on submit.
    let valid = this.applyInputValidity(this.inputEmail, validation.email);
    valid = valid && this.applyInputValidity(this.inputMessage, validation.message);
    valid = valid && this.applyInputValidity(this.inputName, validation.name);

    if (this.inputCountry) {
      valid = valid && this.applyCountryValidity(validation.country);
    }

    if (!this.loggedIn && this.captchaInput) {
      valid = valid && this.applyInputValidity(this.captchaInput, validation.captcha);
    }

    if (!valid) {
      return false;
    }

    return request;
  }

  private validateCountrySelector(): boolean {
    const valid = Boolean(this.inputCountry && hasValue(this.inputCountry.value));
    return this.applyCountryValidity(valid);
  }

  private applyCountryValidity(valid: boolean): boolean {
    this.countrySelect?.classList.toggle("error", !valid);
    return valid;
  }

  private validateInput(element: HTMLElement | null): boolean {
    if (!element) {
      return false;
    }

    const input = element as HTMLInputElement | HTMLTextAreaElement;
    return this.applyInputValidity(element, hasValue(input.value));
  }

  private invalidateInput(element: HTMLElement | null): void {
    element?.classList.add("error");
  }

  private validateEmail(element: HTMLInputElement | null): boolean {
    if (!element) {
      return false;
    }

    return this.applyInputValidity(element, hasValidEmail(element.value));
  }

  private applyInputValidity(element: HTMLElement | null, valid: boolean): boolean {
    if (!element) {
      return false;
    }

    element.classList.toggle("error", !valid);
    return valid;
  }
}
